<template>
  <div class="right-panel">
    <h4>Reports</h4>
    <div class="col-md-6 d-flex flex-row">
      <div class="col-6 pe-3 me-3 border-end">
        <div class="list-group">
          <a href="#" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Participants') }" aria-current="true"
            @click="reportType = 'Participants'">Participants Report</a>
          <a href="#" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Programs') }" @click="reportType = 'Programs'">{{terms?.ProgramPlural}} Report</a>
          <a href="#" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Payments') }" @click="reportType = 'Payments'">Payment Transaction
            Report</a>
          <a href="#" v-if="isOrgUser" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Registered Facilities') }"
            @click="reportType = 'Registered Facilities'">Registered Facilities Report</a>
          <a href="#" v-if="isOrgUser && settings.showParticipantPack" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Participant Pack') }" aria-current="true"
            @click="reportType = 'Participant Pack'">Participant Pack Report</a>
          <a href="#" v-if="isOrgUser && settings.showActiveKidsDiscount" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Active Kids Discount') }"
            @click="reportType = 'Active Kids Discount'">Active Kids Discount Report</a>
          <a href="#" v-if="isOrgUser" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Participant Voucher') }" aria-current="true"
            @click="reportType = 'Participant Voucher'">National Vouchers Report</a>
          <a href="#" v-if="isOrgUser && settings.showHatDistributionReport" class="list-group-item list-group-item-action"
            :class="{ 'active': showForReportTypes('Hat Distribution Report') }" aria-current="true"
            @click="reportType = 'Hat Distribution'">Hat Distribution Report</a>            
        </div>
      </div>
      <div class="col-6">
        <div class="mb-2" v-if="showForReportTypes('Participants', 'Programs')">
          <label for="programCategory" class="form-label">Category:</label>
          <select class="form-select" id="programCategory" v-model="selectedCategoryId" @change="loadPrograms"
            :disabled="isLoadingCategories">
            <option v-for="category in categories" :value="category.id">{{ category.description }}</option>
          </select>
        </div>
        <div class="mb-2" v-if="isOrgUser && showForReportTypes('Participants', 'Programs')">
          <label for="region" class="form-label">{{ settings ? settings.stateName : 'State' }}:</label>
          <select class="form-select" id="region" v-model="selectedRegionId" :disabled="isLoadingRegionsSelector">
            <option :value="undefined">-- All --</option>
            <option v-for="item in regionsSelector" :value="item.key">{{ item.value }}</option>
          </select>
        </div>
        <div class="mb-2" v-if="isOrgUser && showForReportTypes('Participants', 'Programs', 'Payments')">
          <label for="venue" class="form-label">Venue:</label>
          <select class="form-select" id="venue" v-model="selectedVenueId" @change="loadPrograms"
            :disabled="isLoadingVenueSelector">
            <option :value="undefined">-- All --</option>
            <option v-for="item in venuesSelector" :value="item.key">{{ item.value }}</option>
          </select>
        </div>
        <div class="mb-2" v-if="isOrgUser && showForReportTypes('Payments')">
          <label for="program" class="form-label">{{terms?.Program}}:</label>
          <select class="form-select" id="program" v-model="selectedProgramId" :disabled="isLoadingPrograms">
            <option :value="undefined">-- All --</option>
            <option v-for="item in programsSelector" :value="item.key">{{ item.value }}</option>
          </select>
        </div>
        <div class="mb-2" v-if="!showForReportTypes('Registered Facilities')">
          <div v-if="dateOption == 'fiscalyear'">
            <label for="fiscalyear" class="form-label">Financial Year:</label>
            <select class="form-select" id="fiscalyear" v-model="selectedFiscalYear">
              <option v-for="item in fiscalYearSelector" :value="item.key">{{ item.value }}</option>
            </select>
          </div>
          <template v-if="dateOption == 'daterange'">
            <div>
              <label for="dateFrom" class="form-label">Date From:</label>
              <VueDatePicker v-model="dateFrom" format="dd MMM yyyy" :clearable="true" :enable-time-picker="false"
                auto-apply placeholder="DD Mon YYYY" />
            </div>
            <div>
              <label for="dateFrom" class="form-label">Date To:</label>
              <VueDatePicker v-model="dateTo" format="dd MMM yyyy" :clearable="true" :enable-time-picker="false"
                auto-apply placeholder="DD Mon YYYY" />
            </div>
          </template>
          <div class="mt-2">
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="date" id="fiscalyearopt" value="fiscalyear"
                v-model="dateOption">
              <label class="form-check-label" for="fiscalyearopt">Financial Year</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="date" id="daterangeopt" value="daterange"
                v-model="dateOption">
              <label class="form-check-label" for="daterangeopt">Date Range</label>
            </div>
          </div>
        </div>
        <div class="mt-4">
          <a :href="reportUrl" target="_blank" class="btn btn-primary" :disabled="!reportUrl">Open Report</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth-store';
import { useProgramCategoriesStore } from '@/stores/program-categories-store';
import { useProgramStore } from '@/stores/program-store';
import { useVenueStore } from '@/stores/venue-store';
import { type ISelectItem } from '@/types';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { ref } from 'vue';
import { useSettingStore } from "@/stores/setting-store";
import { useTermsStore } from "@/stores/language-terms";
const { terms } = storeToRefs(useTermsStore());

const { getSetting } = useSettingStore();
const { setting } = storeToRefs(useSettingStore());
const settings = ref(setting);

const { orgUrl, liveUrl, isOrgUser } = storeToRefs(useAuthStore());
const { categories, isLoadingCategories } = storeToRefs(useProgramCategoriesStore());
const { venuesSelector, isLoadingVenueSelector } = storeToRefs(useVenueStore());
const { programsSelector, isLoadingPrograms, regionsSelector, isLoadingRegionsSelector } = storeToRefs(useProgramStore());

const { loadProgramCategories } = useProgramCategoriesStore();
const { getVenuesSelector } = useVenueStore();
const { loadProgramsSelector, loadRegionsSelector } = useProgramStore();

const selectedRegionId = ref<number>();
const selectedCategoryId = ref<number>();
const selectedVenueId = ref<number | undefined>(undefined);
const selectedProgramId = ref<number | undefined>(undefined);
const dateOption = ref('fiscalyear');
const selectedFiscalYear = ref<string>();
const dateFrom = ref<Date>();
const dateTo = ref<Date>();
const dateFromPrevFY = ref<Date>();
const dateToPrevFY = ref<Date>();
const fiscalYear = computed(() => {
  return dateOption.value == 'fiscalyear' && selectedFiscalYear.value ? `${moment(dateFrom.value).year()}-${moment(dateTo.value).year()}` : '';
});

const participantsReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.ParticipantsReport&ProgramCategoryId=${selectedCategoryId.value}&venueId=${selectedVenueId.value ? selectedVenueId.value : ''}&regionId=${selectedRegionId.value ? selectedRegionId.value : ''}&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const programsReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.ProgramsReport&ProgramCategoryId=${selectedCategoryId.value}&regionId=${selectedRegionId.value ? selectedRegionId.value : ''}&venueId=${selectedVenueId.value ? selectedVenueId.value : ''}&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const paymentsReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.PaymentsReport&venueId=${selectedVenueId.value ? selectedVenueId.value : ''}&programId=${selectedProgramId.value ? selectedProgramId.value : ''}&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const registeredFacilitiesReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.RegisteredFacilitiesReport&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FromDatePrevFY=${moment(dateFromPrevFY.value).format('YYYY-MM-DD')}&ToDatePrevFY=${moment(dateToPrevFY.value).format('YYYY-MM-DD')}`;
});

const participantPackReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.ParticipantPackReport&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const activeKidsDiscountReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.ParticipationActiveKidsDiscountReport&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const participantVoucherReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.ParticipantVoucherReport&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const hatDistributionReportUrl = computed(() => {
  return `${isOrgUser.value ? orgUrl.value : liveUrl.value}Shared/Reports/ExtQueryRender.aspx?ExtQueryResourceName=Participation.ParticipationHatDistributionReport&FromDate=${moment(dateFrom.value).format('YYYY-MM-DD')}&ToDate=${moment(dateTo.value).format('YYYY-MM-DD')}&FinancialYear=${fiscalYear.value}`;
});

const reportUrl = computed(() => {
  if (reportType.value == "Participants") {
    return participantsReportUrl.value;
  } else if (reportType.value == "Programs") {
    return programsReportUrl.value;
  } else if (reportType.value == "Payments") {
    return paymentsReportUrl.value;
  } else if (reportType.value == "Registered Facilities") {
    updatePrevFYDateValues(fiscalYearSelector.value[1].key);
    return registeredFacilitiesReportUrl.value;
  } else if (reportType.value == "Participant Pack") {
    return participantPackReportUrl.value;
  } else if (reportType.value == "Active Kids Discount") {
    return activeKidsDiscountReportUrl.value;
  } else if (reportType.value == "Participant Voucher") {
    return participantVoucherReportUrl.value;
  } else if (reportType.value == "Hat Distribution") {
    return hatDistributionReportUrl.value;
  }
  return '';
});


watch(selectedFiscalYear, () => {
  updateDateValues();
});

watch(dateOption, () => {
  updateDateValues();
});

const updateDateValues = () => {

  if (dateOption.value != 'fiscalyear') {
    return;
  }

  let startDate = moment('2024-07-01', 'YYYY-MM-DD')
  let toDate = moment('2025-06-30', 'YYYY-MM-DD')
  if (settings.value) {
    startDate = moment(settings.value.financialYearEndReportStartDate, 'YYYY-MM-DD');
    toDate = moment(settings.value.financialYearEndReportEndDate, 'YYYY-MM-DD');
  }

  const selectorValue = fiscalYearSelector.value.find(x => x.key == selectedFiscalYear.value)
  if (selectorValue) {
    const year = selectorValue.value;
    const yearArr = year.split('-');
    startDate.set({ 'year': parseInt(yearArr[0]) });
    toDate.set({ 'year': parseInt(yearArr[1]) });

    dateFrom.value = startDate.toDate();
    dateTo.value = toDate.toDate();
  }
}

const updatePrevFYDateValues = (value: string | undefined) => {
  if (value) {
    const arr = value.split(':');
    dateFromPrevFY.value = moment(arr[0], 'YYYY-MM-DD').toDate();
    dateToPrevFY.value = moment(arr[1], 'YYYY-MM-DD').toDate();
  }
}

const fiscalYearSelector = computed(() => {
  var arr: ISelectItem[] = [];
  var isDisplayNextYear = (new Date().getMonth() >= 6);
  const currentYear = new Date().getFullYear() + (isDisplayNextYear ? 1 : 0);
  for (let i = 0; i <= currentYear - 2015; i++) {
    arr.push({ key: `${currentYear - (i + 1)}-07-01:${currentYear - i}-06-30:${currentYear - (i + 1)}-${currentYear - i}`, value: `${currentYear - (i + 1)}-${currentYear - i}` })
  }
  return arr;
});

const reportType = ref('Participants');
const showForReportTypes = (...params: string[]) => {
  return params.indexOf(reportType.value) >= 0;
}

const loadPrograms = async () => {
  selectedProgramId.value = undefined;
  if (isOrgUser.value && selectedCategoryId.value && selectedVenueId.value) {
    await loadProgramsSelector(selectedCategoryId.value, selectedVenueId.value);
  }
}

onMounted(async () => {
  await loadProgramCategories();
  await loadRegionsSelector();
  await getSetting();
  selectedCategoryId.value = categories.value[0].id;
  if (isOrgUser.value) {
    await getVenuesSelector(true);
    await loadPrograms();
  }

  selectedFiscalYear.value = fiscalYearSelector.value[0].key;
  if (settings.value) {
    dateFrom.value = moment(settings.value.financialYearEndReportStartDate, 'YYYY-MM-DD').toDate();
    dateTo.value = moment(settings.value.financialYearEndReportEndDate, 'YYYY-MM-DD').toDate();
  }
})

</script>

<style scoped>
.reports-listing {
  width: 400px;
}
</style>